import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, RetirementReasonService } from '/src/api';
import { Box, BoxProps, Input, Select } from '/src/components';
import { useGlobalStore } from '/src/context';
import { OTHER_RETIREMENT_REASON_NAME, RetirementReason, TRetirementReasonId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { SelectOptions } from '/src/lib/types';

const MAX_REASON_TEXT_LENGTH = 500;

export type RetirementReasonTextFieldProps = {
  value: string;
  setValue: (value: string) => void;
  error?: string;
};

export const RetirementReasonTextField: React.FC<RetirementReasonTextFieldProps> = ({ value, setValue, error }) => {
  const { t } = useTranslation();

  return (
    <Input
      id="retirement_reason_other"
      label={t('common:otherReasonDescription')}
      value={value}
      setValue={setValue}
      error={error}
      componentType="textArea"
      maxLength={MAX_REASON_TEXT_LENGTH}
      rows={1}
      required
      fill="horizontal"
      centerLabel={false}
      placeholder=""
    />
  );
};

export type RetirementReasonSelectProps = {
  entityType: 'equipment' | 'facility';
  reasonId?: TRetirementReasonId;
  setReasonId: (reasonId?: TRetirementReasonId) => void;
  reasonText?: string;
  setReasonText: (reasonText: string) => void;
  setIsOther?: (isOther: boolean) => void;
  reasonError?: string;
  textError?: string;
  disabled?: boolean;
  showTextField?: boolean;
  fill?: BoxProps['fill'];
  width?: BoxProps['width'];
};

export const RetirementReasonSelect: React.FC<RetirementReasonSelectProps> = (props) => {
  const {
    entityType,
    reasonId,
    setReasonId,
    reasonText = '',
    setReasonText,
    setIsOther,
    reasonError,
    textError,
    disabled,
    showTextField = true,
    fill,
    ...selectProps
  } = props;

  /** Context **/
  const globalStore = useGlobalStore();
  const { t } = useTranslation();

  /** State **/
  const [reasons, setReasons] = useState<RetirementReason[]>();

  /** Methods **/
  const fetchReasons = async () => {
    try {
      const data = await RetirementReasonService.list(
        entityType === 'equipment' ? { visible_to_equipment: true } : { visible_to_facilities: true }
      );
      setReasons(data);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.generic.error);
    }
  };

  const isOtherReason = (id?: TRetirementReasonId) =>
    (reasons ?? []).find((reason) => reason.id === id)?.name === OTHER_RETIREMENT_REASON_NAME;

  const handleReasonChange = (value?: TRetirementReasonId) => {
    setReasonId(value);
    const isOther = isOtherReason(value);
    setIsOther?.(isOther);
    if (!isOther) setReasonText('');
  };

  /** Effects **/
  useEffect(() => {
    fetchReasons();
  }, [entityType]);

  /** Render **/
  const options: SelectOptions<TRetirementReasonId> = (reasons ?? []).map((reason) => ({
    label: reason.name,
    value: reason.id,
  }));

  return (
    <Box gap="small" fill={fill}>
      <Select
        id="retirement_reason_id"
        label={t('common:reasonForRetiring')}
        placeholder={reasons ? t('common:selectReason') : t('common:loading')}
        value={reasonId}
        setValue={handleReasonChange}
        options={options}
        error={reasonError}
        disabled={disabled || !reasons}
        required
        fill="horizontal"
        {...selectProps}
      />
      {showTextField && isOtherReason(reasonId) && (
        <RetirementReasonTextField value={reasonText} setValue={setReasonText} error={textError} />
      )}
    </Box>
  );
};
